// Image Studio — chat-drawer-as-page replacement for the retired
// /image-gen wizard. Owner directive 2026-10-14 (option 1):
// "replace /image-gen with /image-studio, driven by the producer agent".
//
// Layout:
//   • Left column — catalog grid with per-product persona-coverage.
//     Operators pick which products they want to talk about by
//     checkbox; the selection drives the chat's dynamic suggestions.
//   • Right column — AgentChat scoped to the active brand, with
//     image-studio-tailored starter suggestions that fold in the
//     operator's current selection (product names + brand name).
//
// The chat dispatches catalog.generateLifestyleImages through the
// standard Tier 4 agent path, so it inherits everything the capability
// already exposes: styles[] + countPerSegment (Phase 1) and
// useProducerSeedPicker (Phase 4), zero duplicate code in the UI. The
// previous page's inline Generate/confirm/poll loop is deliberately
// gone — the chat's own WorkflowResultCard renders the plan, confirm
// gate, and per-cell progress for the same workflow.

import {
  Badge, Box, Button, Card, CardBody, Grid, GridItem, HStack, Heading,
  Input, InputGroup, InputLeftElement, Spinner, Text, VStack
} from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AgentChat } from '../../agent';
import { readGenerateHandoff } from '../GenerateAds/handoff';
import { useBrandDetail } from '../Brand/useBrandDetail';
import { CoverageRow } from './CoverageRow';
import { fetchSummary, SummaryProduct, SummaryResponse } from './api';

const PAGE_LIMIT = 25;

export function ImageStudioPage() {
  const [params] = useSearchParams();
  const handoff = readGenerateHandoff(params);
  const { brand, loading: brandLoading, error: brandError } = useBrandDetail();
  const brandId = brand?._id;
  const brandName = brand?.name || null;

  const [searchInput, setSearchInput] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [offset, setOffset] = useState(0);

  const [summary, setSummary] = useState<SummaryResponse | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState<string | null>(null);

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Debounce search input (300ms) so typing doesn't thrash the summary endpoint.
  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Filter change resets pagination + selection.
  useEffect(() => {
    setOffset(0);
    setSelectedIds(new Set());
  }, [searchDebounced]);

  // Fetch summary on brand / filter / offset change.
  useEffect(() => {
    if (!brandId) { setSummary(null); return; }
    let cancelled = false;
    setSummaryLoading(true);
    fetchSummary(brandId, { search: searchDebounced, limit: PAGE_LIMIT, offset })
      .then(data => { if (!cancelled) { setSummary(data); setSummaryError(null); } })
      .catch(e => { if (!cancelled) setSummaryError(e?.message || 'summary failed'); })
      .finally(() => { if (!cancelled) setSummaryLoading(false); });
    return () => { cancelled = true; };
  }, [brandId, searchDebounced, offset]);

  const toggleSelect = useCallback((productId: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(productId)) next.delete(productId); else next.add(productId);
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  const handoffIds = handoff.productIds.join(',');
  useEffect(() => {
    const ids = handoffIds ? handoffIds.split(',') : [];
    if (!ids.length) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      let changed = false;
      for (const id of ids) {
        if (!next.has(id)) { next.add(id); changed = true; }
      }
      return changed ? next : prev;
    });
  }, [handoffIds]);

  // Resolve selected product summaries from the current page's data.
  // A selection whose product isn't on-page (operator paginated or
  // filtered) stays in the set but is simply not named — the chat still
  // sees the brand scope.
  const selectedProducts = useMemo<SummaryProduct[]>(() => {
    if (!summary) return [];
    const idSet = selectedIds;
    return summary.products.filter(p => idSet.has(p.productId));
  }, [summary, selectedIds]);

  // Dynamic starter suggestions — regenerate whenever the selection
  // changes. When nothing is selected the suggestions nudge the operator
  // toward the grid; once something is selected they fold the product
  // NAMES (not IDs — operator-facing) into actionable prompts covering
  // the Phase 1 + Phase 4 levers.
  const suggestions = useMemo(() => buildSuggestions(selectedProducts, brandName), [selectedProducts, brandName]);

  if (brandLoading && !brand) {
    return <Card variant="outline"><CardBody><HStack><Spinner /><Text fontSize="sm" color="brand.muted">Loading brand…</Text></HStack></CardBody></Card>;
  }
  if (brandError) {
    return <Card variant="outline"><CardBody><Text color="red.600" fontSize="sm">Failed to load brand: {brandError}</Text></CardBody></Card>;
  }
  if (!brandId) {
    return <Card variant="outline"><CardBody><Text fontSize="sm" color="brand.muted">No active brand.</Text></CardBody></Card>;
  }

  return (
    <Box pb={4}>
      <VStack align="stretch" spacing={3} mt={3}>
        <Box>
          <Heading size="md">Image Studio</Heading>
          <Text fontSize="sm" color="brand.muted" mt={1}>
            Chat-driven lifestyle imagery. Pick products on the left for context, then ask the producer agent to generate — multiple styles (lifestyle, on_model, flat_lay, detail, packaging), N variants per segment, and an opt-in LLM seed picker per segment.
          </Text>
          {handoff.capUsd != null && (
            <Text fontSize="sm" mt={2} data-testid="lifestyle-spend-cap">
              Spend cap ${handoff.capUsd.toFixed(2)}. Nothing runs until you confirm it in the chat, and a plan whose estimate is above this cap stays blocked.
            </Text>
          )}
        </Box>

        {/* Grid left, chat right. Stacks on mobile. The chat column
            needs an explicit height so AgentChat's fill mode flexes. */}
        <Grid
          templateColumns={{ base: '1fr', lg: 'minmax(0, 1.1fr) minmax(0, 1fr)' }}
          gap={4}
          h={{ base: 'auto', lg: 'calc(100vh - 14rem)' }}
          minH={{ base: 'auto', lg: '560px' }}
        >
          {/* ── LEFT: catalog grid ───────────────────────────────── */}
          <GridItem overflow={{ base: 'visible', lg: 'auto' }}>
            <Card variant="outline" h="100%">
              <CardBody display="flex" flexDirection="column" gap={3} overflow="hidden">
                <HStack spacing={3} align="center" flexWrap="wrap">
                  <InputGroup size="sm" maxW="280px">
                    <InputLeftElement pointerEvents="none">
                      <Text fontSize="xs" color="brand.muted">🔍</Text>
                    </InputLeftElement>
                    <Input
                      placeholder="Search products by title…"
                      value={searchInput}
                      onChange={e => setSearchInput(e.target.value)}
                    />
                  </InputGroup>
                  {summary && (
                    <HStack spacing={2} fontSize="12px" color="brand.muted" flexWrap="wrap">
                      <Badge>{summary.productsTotal} products</Badge>
                      <Badge>{summary.personas.length} personas</Badge>
                    </HStack>
                  )}
                </HStack>

                {selectedIds.size > 0 && (
                  <HStack
                    bg="purple.50"
                    borderWidth="1px"
                    borderColor="purple.200"
                    borderRadius="md"
                    px={3}
                    py={2}
                    spacing={3}
                    justify="space-between"
                  >
                    <Text fontSize="sm" fontWeight="600" color="purple.800">
                      {selectedIds.size} selected · ready for the chat →
                    </Text>
                    <Button size="xs" variant="ghost" onClick={clearSelection}>
                      Clear
                    </Button>
                  </HStack>
                )}

                {/* Scrollable product list */}
                <Box flex="1" overflowY="auto" minH="200px">
                  {summaryLoading && !summary && (
                    <HStack py={6} justify="center"><Spinner size="sm" /><Text fontSize="sm" color="brand.muted">Loading products…</Text></HStack>
                  )}
                  {summaryError && (
                    <Text fontSize="sm" color="red.600">Failed to load products: {summaryError}</Text>
                  )}
                  {summary && summary.products.length === 0 && (
                    <Text fontSize="sm" color="brand.muted" py={6} textAlign="center">
                      {searchDebounced ? `No products match "${searchDebounced}".` : 'No products in this brand yet — import your catalog first.'}
                    </Text>
                  )}
                  {summary && summary.products.length > 0 && (
                    <VStack align="stretch" spacing={2}>
                      {summary.products.map(product => (
                        <CoverageRow
                          key={product.productId}
                          product={product}
                          isSelected={selectedIds.has(product.productId)}
                          onToggleSelect={() => toggleSelect(product.productId)}
                        />
                      ))}
                    </VStack>
                  )}
                </Box>

                {summary && summary.hasMore && (
                  <HStack justify="center" pt={1}>
                    <Button size="xs" variant="ghost" onClick={() => setOffset(o => o + PAGE_LIMIT)}>
                      Load more
                    </Button>
                  </HStack>
                )}
              </CardBody>
            </Card>
          </GridItem>

          {/* ── RIGHT: AgentChat (brand-scoped, image-studio suggestions) ── */}
          <GridItem minH={{ base: '520px', lg: 'auto' }}>
            <AgentChat
              brandName={brandName}
              brandId={brandId}
              suggestions={suggestions}
              spendCapUsd={handoff.capUsd}
              planId={handoff.creativeType === 'catalog-image' ? handoff.planId : null}
              creativeType={handoff.creativeType === 'catalog-image' ? 'catalog-image' : null}
              fill
            />
          </GridItem>
        </Grid>
      </VStack>
    </Box>
  );
}

// Pure helper, exported for a unit-test-friendly surface. Produces 3-5
// starter prompts covering the common Image Studio patterns. When
// selection is non-empty the prompts name the selected products so the
// agent can resolve them to productIds via its own catalog lookup —
// avoids leaking ObjectId strings into the operator UI.
export function buildSuggestions(selected: SummaryProduct[], brandName: string | null): string[] {
  const subject = brandName ? brandName : 'this brand';
  if (!selected.length) {
    return [
      `What's missing coverage across ${subject}'s catalog?`,
      `Generate lifestyle images for the top 5 uncovered products with the producer seed picker`,
      `Fill coverage gaps for ${subject} across all personas (one lifestyle shot each)`,
      `Show me recent lifestyle generation runs`,
    ];
  }
  const names = selected.slice(0, 3).map(p => p.title || `product ${p.productId.slice(-6)}`);
  const nameList = names.join(', ') + (selected.length > 3 ? `, +${selected.length - 3} more` : '');
  const forPhrase = `the ${selected.length} selected product${selected.length === 1 ? '' : 's'} (${nameList})`;
  return [
    `Generate on_model lifestyle images for ${forPhrase}, 2 variants per persona, with the producer seed picker ON`,
    `Generate all 5 styles (lifestyle, on_model, flat_lay, detail, packaging) for ${forPhrase}, 1 variant per segment`,
    `Fill coverage gaps for ${forPhrase} across all personas`,
    `Try the producer seed picker on ${forPhrase} — on_model only, 1 variant per persona`,
  ];
}

export default ImageStudioPage;
